(() => {
  class PrintsDrop extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.track = this.querySelector('[data-prints-track]');
      this.slides = [...this.querySelectorAll('[data-prints-slide]')];
      this.position = this.querySelector('[data-prints-position]');
      this.querySelector('[data-prints-controls]')?.removeAttribute('hidden');
      this.querySelector('[data-prints-prev]')?.addEventListener('click', () => this.move(-1), { signal });
      this.querySelector('[data-prints-next]')?.addEventListener('click', () => this.move(1), { signal });
      this.track?.addEventListener('scroll', () => this.syncPosition(), { signal, passive: true });
      this.track?.addEventListener('keydown', (event) => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : this.currentIndex() + (event.key === 'ArrowRight' ? 1 : -1);
        this.goTo(index);
      }, { signal });
      this.querySelector('[data-drop-retry]')?.addEventListener('click', () => this.reveal(), { signal });
      this.deadline = Date.parse(this.dataset.dropAt);
      const serverNow = Number(this.dataset.serverNow) * 1000;
      this.startTime = performance.now();
      this.serverTime = Number.isFinite(serverNow) ? serverNow : Date.now();
      const date = this.querySelector('[data-drop-date]');
      if (date && Number.isFinite(this.deadline)) {
        date.textContent = new Intl.DateTimeFormat(document.documentElement.lang || 'fr', { dateStyle: 'long', timeStyle: 'short', timeZone: 'Europe/Paris' }).format(this.deadline);
      }
      if (this.dataset.dropState === 'pending' && Number.isFinite(this.deadline)) {
        this.tick();
        this.timer = window.setInterval(() => this.tick(), 1000);
        document.addEventListener('visibilitychange', () => { if (!document.hidden) this.tick(); }, { signal });
      }
    }

    disconnectedCallback() {
      this.abort?.abort();
      clearInterval(this.timer);
      clearTimeout(this.retryTimer);
    }

    currentIndex() {
      if (!this.track || !this.slides.length) return 0;
      const left = this.track.getBoundingClientRect().left;
      return this.slides.reduce((best, slide, index) => Math.abs(slide.getBoundingClientRect().left - left) < Math.abs(this.slides[best].getBoundingClientRect().left - left) ? index : best, 0);
    }

    syncPosition() {
      if (this.position) this.position.textContent = `${this.currentIndex() + 1} / ${this.slides.length}`;
    }

    move(direction) { this.goTo(this.currentIndex() + direction); }

    goTo(index) {
      if (!this.slides.length) return;
      const slide = this.slides[(index + this.slides.length) % this.slides.length];
      this.track.scrollBy({ left: slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }

    tick() {
      const now = this.serverTime + performance.now() - this.startTime;
      const seconds = Math.max(0, Math.ceil((this.deadline - now) / 1000));
      const units = { days: Math.floor(seconds / 86400), hours: Math.floor(seconds / 3600) % 24, minutes: Math.floor(seconds / 60) % 60, seconds: seconds % 60 };
      for (const [unit, value] of Object.entries(units)) {
        const node = this.querySelector(`[data-drop-unit="${unit}"]`);
        if (node) node.textContent = String(value).padStart(2, '0');
      }
      this.querySelector('[data-drop-clock]')?.toggleAttribute('hidden', seconds === 0);
      if (seconds === 0) {
        clearInterval(this.timer);
        this.querySelector('[data-drop-waiting]')?.removeAttribute('hidden');
        this.reveal();
      }
    }

    async reveal() {
      if (this.loading || !this.isConnected || this.dataset.dropState !== 'pending') return;
      clearTimeout(this.retryTimer);
      this.loading = true;
      this.attempts = (this.attempts || 0) + 1;
      this.querySelector('[data-drop-retry]')?.setAttribute('hidden', '');
      try {
        const release = await fetch('/apps/blankabric-wishlist/prints/status', { signal: this.abort.signal, cache: 'no-store' });
        if (!release.ok) throw new Error('Drop pending');
        const url = new URL(location.href);
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set('_drop', String(Date.now()));
        const response = await fetch(url, { signal: this.abort.signal, cache: 'no-store' });
        if (!response.ok) throw new Error('Drop unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = doc.querySelector('blankabric-prints-drop');
        if (!fresh || fresh.dataset.dropState === 'pending' && Date.parse(fresh.dataset.dropAt) <= this.deadline) throw new Error('Drop pending');
        const region = fresh.querySelector('[data-drop-region]');
        if (!region) throw new Error('Drop unavailable');
        const hadFocus = this.querySelector('[data-drop-region]')?.contains(document.activeElement);
        this.replaceWith(fresh);
        if (Date.parse(fresh.dataset.dropAt) > this.deadline) this.refreshCollection();
        const heading = fresh.querySelector('[data-drop-region] h2');
        if (hadFocus && heading) { heading.tabIndex = -1; heading.focus({ preventScroll: true }); }
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (this.attempts < 12) this.retryTimer = setTimeout(() => this.reveal(), Math.min(60000, 5000 * 2 ** (this.attempts - 1)));
        if (this.attempts >= 4) this.querySelector('[data-drop-retry]')?.removeAttribute('hidden');
      } finally { this.loading = false; }
    }

    refreshCollection() { location.reload(); }
  }

  class PrintsShortcuts extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const current = new URL(location.href);
      this.querySelectorAll('a').forEach(link => {
        const url = new URL(current);
        url.searchParams.delete('page');
        if (link.dataset.printsSort) {
          url.searchParams.set('sort_by', link.dataset.printsSort);
          if (current.searchParams.get('sort_by') === link.dataset.printsSort) link.setAttribute('aria-current', 'true');
        } else {
          const param = link.dataset.param;
          url.searchParams.delete(param);
          if (link.getAttribute('aria-current') !== 'true') {
            new URLSearchParams(link.dataset.values).getAll(param).forEach(value => url.searchParams.append(param, value));
            url.searchParams.set('filter.v.availability', '1');
            url.searchParams.delete('bb_stock');
          }
        }
        url.hash = 'ResultsList';
        link.href = url.toString();
      });
      this.addEventListener('click', event => {
        const range = event.target.closest('[data-prints-range]');
        if (range) {
          const url = new URL(location.href);
          if (url.searchParams.get('bb_length') === range.dataset.printsRange) url.searchParams.delete('bb_length');
          else url.searchParams.set('bb_length', range.dataset.printsRange);
          url.searchParams.delete('page');
          history.pushState(history.state, '', url);
          this.applyRange();
          return;
        }
        const link = event.target.closest('a');
        if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const facets = this.closest('results-list')?.querySelector('facets-form-component');
        if (!facets?.updateFiltersByURL) return;
        event.preventDefault();
        facets.updateFiltersByURL(link.href);
      }, { signal: this.abort.signal });
      if (this.querySelector('[data-prints-range]')) {
        this.results = this.closest('results-list');
        this.status = this.querySelector('[data-prints-length-status]');
        window.addEventListener('popstate', () => this.applyRange(), { signal: this.abort.signal });
        this.results?.addEventListener('bb:results-updated', () => { this.filterCards(); if (!this.loading) this.applyRange(); }, { signal: this.abort.signal });
        document.addEventListener('filter:update', () => { this.runAbort?.abort(); this.loading = false; this.completed = false; }, { signal: this.abort.signal });
        customElements.whenDefined('results-list').then(() => {
          if (!this.isConnected || !this.results?.loadAllPages) return;
          this.querySelectorAll('[data-prints-range]').forEach(button => button.hidden = false);
          this.applyRange();
        });
      }
    }

    group() {
      const group = new URL(location.href).searchParams.get('bb_length');
      return ['long', 'medium', 'short'].includes(group) ? group : null;
    }

    filterCards() {
      const group = this.group();
      this.results?.toggleAttribute('data-print-length-active', Boolean(group));
      this.querySelectorAll('[data-prints-range]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.printsRange === group)));
      let count = 0;
      this.results?.querySelectorAll('#ResultsList > [data-product-id]').forEach(card => {
        let matches = !group;
        if (group) {
          const fields = [...card.querySelectorAll('[data-bb-option-field]')];
          let position = fields.findIndex(field => /longueur|length|länge|lange|laenge|longitud|lunghezza|lengte|comprimento/.test(field.dataset.optionName || ''));
          if (position < 0 && fields.length === 1) position = 0;
          try {
            matches = JSON.parse(card.querySelector('[data-bb-variants]')?.textContent || '[]').some(variant => {
              const raw = String(variant.options?.[position] || '').trim().replace(',', '.');
              const length = Number.parseFloat(raw);
              if (!variant.available || !/^\d+(?:\.\d+)?\s*(m|mètres?|metres?|meters?|metri|metros?)$/i.test(raw)) return false;
              return group === 'long' ? length >= 3 : group === 'medium' ? length >= 2 && length < 3 : length > 0 && length < 2;
            });
          } catch { matches = false; }
        }
        card.classList.toggle('bb-print-length-hidden', !matches);
        if (matches) count++;
      });
      if (this.status) {
        this.status.hidden = !group;
        if (group) this.status.textContent = this.loading ? this.status.dataset.loading : this.completed ? this.status.dataset.count.replace('__COUNT__', String(count)) : this.status.dataset.error;
      }
    }

    async applyRange() {
      this.filterCards();
      if (!this.group() || this.loading || !this.results?.loadAllPages) return;
      this.runAbort = new AbortController();
      const run = this.runAbort;
      this.loading = true;
      this.filterCards();
      this.setAttribute('aria-busy', 'true');
      try { this.completed = await this.results.loadAllPages(run.signal); }
      catch { this.completed = false; }
      finally {
        if (run !== this.runAbort) return;
        this.loading = false;
        this.removeAttribute('aria-busy');
        this.filterCards();
      }
    }

    disconnectedCallback() { this.abort?.abort(); this.runAbort?.abort(); }
  }

  if (!customElements.get('blankabric-prints-drop')) customElements.define('blankabric-prints-drop', PrintsDrop);
  if (!customElements.get('blankabric-prints-shortcuts')) customElements.define('blankabric-prints-shortcuts', PrintsShortcuts);
})();
